'use client'

import { ProductCarousel } from '@/app/components/shared/ProductCarousel'
import type { ProductCarouselAnalytics } from '@/app/components/shared/ProductCarousel/ProductCarousel'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NOT_SET } from '@/app/lib/analytics/constants'
import type { ContextualProductListAnalytics, EcommerceItemBase } from '@/app/lib/analytics/types'
import type { ProductCard } from '@/app/lib/api/mapProductToCard'

interface ContextualProductCarouselProps {
  readonly items: readonly ProductCard[]
  /** Config contextual (`item_list_id` = `<page_type>_<module_type>`), con `items` alineado con las cards. */
  readonly analytics: ContextualProductListAnalytics
}

/**
 * `ProductCarousel` instrumentado para un módulo CMS general (Server Component)
 * que no conoce la página: resuelve aquí el `item_list_id` desde el `page_type`
 * del `PageAnalyticsProvider` (misma fórmula que `TrackedProductGrid`).
 *
 * `ProductCarousel` exige un ítem por card; si alguna card no corresponde a un
 * evento (p. ej. un item `"Página"` del módulo «Productos») no se instrumenta el
 * carril entero en vez de desalinear índices. Fuera del provider no emite nada.
 */
export function ContextualProductCarousel({ items, analytics }: ContextualProductCarouselProps) {
  const pageAnalytics = useOptionalPageAnalytics()

  const bases = analytics.items.filter((item): item is EcommerceItemBase => item !== null)
  const config: ProductCarouselAnalytics | undefined =
    bases.length === items.length
      ? {
          list: {
            itemListId: `${pageAnalytics?.context.page_type ?? NOT_SET}_${analytics.moduleType}`,
            itemListName: analytics.itemListName,
            moduleName: analytics.moduleName,
            moduleType: analytics.moduleType,
            modulePosition: analytics.modulePosition,
          },
          items: bases,
        }
      : undefined

  return <ProductCarousel items={items} analytics={config} />
}
